// Lampen ab- und anbauen (15.10.2026; seit 30.10.2026 Teil der Seite Leistungen statt eigener Schritt). Wer „Lampen
// ab- und anbringen“ ankreuzt, gibt je Raum die Lampen mit Art an (Piktogramme), Abbau in der alten und
// Anbau in der neuen Wohnung getrennt, je Lampe ein Foto (Handy nach oben). Dazu neue Lampen nur für die neue Wohnung.
// Die Antwort setzt auch die Leistung „Lampen ab- und anbringen“; die Firma rechnet Minuten je Art (Rechenwerte).

import { LAMP_KINDS, LAMP_KIND_LABEL, type LampItem, type LampKind, type LampsInfo } from "@umzugsscan/shared";
import { byFloorPlan } from "../ablauf.ts";
import { useDraft, useStore } from "../hooks.ts";
import { newId } from "../store.ts";
import { FotoReihe } from "../ui/Galerie.tsx";
import { Button, Check, Stepper } from "../ui/elemente.tsx";
import { Kamera } from "../ui/Kamera.tsx";

const LEER: LampsInfo = { wanted: null, items: [], neu: {} };

/** Kleine Piktogramme je Lampenart (Decke oben, Wand links). */
const PIKTO: Record<LampKind, string> = {
  plafonniere: "M2 4 H22 M6 4 Q12 11 18 4",
  pendel: "M2 3 H22 M12 3 V11 M6 17 L12 11 L18 17 Z",
  kronleuchter: "M2 3 H22 M12 3 V8 M4 11 Q12 18 20 11 M4 11 V15 M12 14.5 V18 M20 11 V15 M8 13.5 V17 M16 13.5 V17",
  strahler: "M2 4 H22 M6 4 V7 L4 11 M12 4 V7 V11 M18 4 V7 L20 11",
  ventilator: "M2 3 H22 M12 3 V9 M3 11 H21 M12 11 V14 M10 14 H14",
  wandleuchte: "M4 3 V21 M4 12 H8 M8 8 L14 6 V18 L8 16 Z",
};
export function LampenPikto(props: { kind: LampKind }) {
  return (
    <svg viewBox="0 0 24 22" width="26" height="24" aria-hidden="true">
      <path d={PIKTO[props.kind]} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Lampen-Angaben des Kunden mit Speichern; `wanted` setzt auch die Leistung „Lampen ab- und anbringen“. */
export function useLampen() {
  const d = useDraft();
  const store = useStore();
  const l: LampsInfo = { ...LEER, ...d.survey.lamps };
  const set = (patch: Partial<LampsInfo>) => {
    const next = { ...l, ...patch };
    store.updateSurvey({ lamps: next, ...(patch.wanted !== undefined && patch.wanted !== null ? { services: { lampen: patch.wanted } } : {}) });
  };
  return { l, set };
}

/** Lampen je Raum (seit 30.10.2026 unter „Lampen ab- und anbringen“ auf der Seite Leistungen, keine eigene Seite). */
export function LampenAngaben() {
  const d = useDraft();
  const store = useStore();
  const { l, set } = useLampen();
  const setItem = (id: string, patch: Partial<LampItem>) => set({ items: l.items.map((x) => (x.id === id ? { ...x, ...patch } : x)) });
  const rooms = [...d.rooms].sort(byFloorPlan);

  return (
    <>
      <p className="hint">Tippen Sie je Raum die Lampen an und machen Sie von jeder ein Foto, Handy nach oben. Nur Lampen, die abgebaut oder angebracht werden sollen.</p>
      {rooms.map((r) => {
        const items = l.items.filter((x) => x.roomId === r.id);
        const fotos = d.photos.filter((p) => p.roomId === r.id && p.step === "lampe");
        return (
          <section className="card lampen-raum" key={r.id}>
            <h2>{r.name}</h2>
            {items.map((it, i) => (
              <div className="lampe" key={it.id}>
                <div className="lampe-kopf">
                  <span className="label">Lampe {i + 1}</span>
                  <button type="button" className="raum-weg" aria-label={`Lampe ${i + 1} in ${r.name} entfernen`} onClick={() => set({ items: l.items.filter((x) => x.id !== it.id) })}>
                    ×
                  </button>
                </div>
                <div className="lampen-arten" role="group" aria-label="Art der Lampe">
                  {LAMP_KINDS.map((k) => (
                    <button key={k} type="button" className={`lampen-art${it.kind === k ? " on" : ""}`} aria-pressed={it.kind === k} onClick={() => setItem(it.id, { kind: k })}>
                      <LampenPikto kind={k} />
                      <span>{LAMP_KIND_LABEL[k]}</span>
                    </button>
                  ))}
                </div>
                <Check checked={it.abbau} onChange={(v) => setItem(it.id, { abbau: v })}>
                  In der alten Wohnung abbauen
                </Check>
                <Check checked={it.anbau} onChange={(v) => setItem(it.id, { anbau: v })}>
                  In der neuen Wohnung wieder anbringen
                </Check>
              </div>
            ))}
            <Button variant="quiet" onClick={() => set({ items: [...l.items, { id: newId(), roomId: r.id, kind: "plafonniere", abbau: true, anbau: true }] })}>
              + Lampe{items.length > 0 ? " hinzufügen" : ` in ${r.name}`}
            </Button>
            {items.length > 0 && (
              <>
                <Kamera roomId={r.id} step="lampe" zoomHint={false} label={fotos.length > 0 ? "📷 Weitere Lampe fotografieren" : "📷 Lampe fotografieren (Handy nach oben)"} />
                <FotoReihe photos={fotos} onDelete={(p) => store.deletePhoto(p.id)} />
              </>
            )}
          </section>
        );
      })}
      <section className="card">
        <h2>Neue Lampen für die neue Wohnung</h2>
        <p className="hint">Lampen, die nur in der neuen Wohnung angebracht werden sollen (z. B. neu gekauft).</p>
        {LAMP_KINDS.map((k) => (
          <Stepper key={k} label={LAMP_KIND_LABEL[k]} value={l.neu[k] ?? 0} max={30} onChange={(v) => set({ neu: { ...l.neu, [k]: v } })} />
        ))}
      </section>
    </>
  );
}
